import requestService from '@/services/request'
import {createSlice} from '@reduxjs/toolkit'
import constants from '@/constants'
import {getCurrencyMode} from '@/util/currencies'
import {addMarketsToPool} from '@/store/markets'
import {addTagsToPool} from '@/store/tags'
import {addCategoriesToPool} from '@/store/categories'
import {toastService} from "@/api/toast";
import { buildErrorToast } from '@/util/errorMessages'

let currentPageTagsAbortController = null

const initialState = {
  categories: { pagination: {}, data: [], status: constants.IDLE },
  forecasters: { pagination: {}, data: [], status: constants.IDLE },
  markets: { pagination: {}, data: [], status: constants.IDLE },
  tags: { pagination: {}, data: [], status: constants.IDLE },
  /* The results page's own topic strip. Kept apart from `tags` so the navbar
     dropdown, which rewrites its results on every keystroke, cannot overwrite
     what the page behind it is showing. */
  pageTags: { pagination: {}, data: [], status: constants.IDLE },
}

const searchReset = (searchType, state) => {
  state[searchType].pagination = {}
  state[searchType].error = null
  state[searchType].data = []
  state[searchType].status = constants.IDLE
}

const searchStart = (searchType, state) => {
  state[searchType].status = constants.LOADING
}

const searchError = (searchType, state, error) => {
  if (!state[searchType]) return
  state[searchType].error = error
  state[searchType].status = constants.ERROR
}

const createQueryString = (query, params) => {
  const {
    ordering,
    live_only,
    categories,
    resolved_only,
    limit,
    offset,
    category,
    people_i_follow,
    currencyId = 'OOM',
  } = params
  let qs = `search=${encodeURIComponent(query)}`

  if (ordering != null && ordering.length > 0) {
    qs += `&ordering=${ordering}`
  }

  if (live_only != null && live_only.length > 0) {
    qs += `&live_only=${live_only}`
  }

  if (resolved_only != null && resolved_only.length > 0) {
    qs += `&resolved_only=${resolved_only}`
  }

  if (categories != null && categories.length > 0) {
    qs += `&categories=${categories}`
  }
  if (category != null && category.length > 0) {
    qs += `&category=${category}`
  }
  if (people_i_follow != null && people_i_follow.length > 0) {
    qs += `&people_i_follow=${people_i_follow}`
  }

  if (limit) {
    qs += `&limit=${limit}`
  }

  if (offset) {
    qs += `&offset=${offset}`
  }

  if (currencyId) {
    qs += '&currency_mode=' + getCurrencyMode(currencyId)
  }

  return qs
}

// Reducers / Slices

const search = createSlice({
  name: 'search',
  initialState,
  reducers: {
    // ? Markets
    searchMarketsReset: state => {
      searchReset('markets', state)
    },

    searchMarketsStart: state => {
      searchStart('markets', state)
    },

    searchMarketsSuccess: (state, action) => {
      const { results, pagination, total } = action.payload

      let markets = [...state.markets.data]

      let current_markets_ids = state.markets.data.map((market) => market.id)

      results.forEach((fetchedMarket) => {
        if(!current_markets_ids.includes(fetchedMarket.id))
        {
          markets.push(fetchedMarket)
        }
      })

      state.markets.data = markets
      state.markets.pagination = pagination ? pagination : { total }
      state.markets.status = constants.COMPLETE
    },

    searchMarketsError: (state, action) => {
      const { error } = action.payload
      searchError('markets', state, error)
    },

    startMarketsLoading: (state) => {
        state.markets.status = constants.LOADING
    },

    searchForecasterReset: state => {
      searchReset('forecasters', state)
    },

    searchForecastersStart: state => {
      searchStart('forecasters', state)
    },

    searchForecastersSuccess: (state, action) => {
      const { results, pagination, total } = action.payload

      let forecasters = [...state.forecasters.data]
      let current_forecasters_ids = state.forecasters.data.map((forecaster) => forecaster.id)

      results.forEach((fetchedForecaster) => {
        if(!current_forecasters_ids.includes(fetchedForecaster.id))
        {
          forecasters.push(fetchedForecaster)
        }
      })

      state.forecasters.data = forecasters
      state.forecasters.pagination = pagination ? pagination : { total }
      state.forecasters.status = constants.COMPLETE
    },

    searchForecastersError: (state, action) => {
      const { error } = action.payload
      searchError('forecasters', error)
    },

    startForecastersLoading: (state) => {
      state.forecasters.status = constants.LOADING
    },

    startTagsLoading: (state) => {
      state.tags.status = constants.LOADING
    },

    // ? Categories
    searchCategoriesReset: state => {
      searchReset('categories', state)
    },

    searchCategoriesStart: state => {
      searchStart('categories', state)
    },

    searchCategoriesSuccess: (state, action) => {
      const { results, pagination, total } = action.payload

      let categories = [...state.categories.data]
      let current_categories_ids = state.categories.data.map((category) => category.id)

      results.forEach((fetchedCategory) => {
        if(!current_categories_ids.includes(fetchedCategory.id))
        {
          categories.push(fetchedCategory)
        }
      })

      state.categories.data = categories
      state.categories.pagination = pagination ? pagination : { total }
      state.categories.status = constants.COMPLETE
    },

    searchCategoriesError: (state, action) => {
      searchError('categories', state, action.payload.error)
    },

    // ? Tags
    searchTagsReset: state => {
      searchReset('tags', state)
    },

    searchTagsStart: state => {
      searchStart('tags', state)
    },

    searchTagsSuccess: (state, action) => {

      const { results, pagination, total } = action.payload

      let tags = [...state.tags.data]
      let current_tags_ids = state.tags.data.map((tag) => tag.id)

      results.forEach((fetchedTag) => {
        if(!current_tags_ids.includes(fetchedTag.id))
        {
          tags.push(fetchedTag)
        }
      })

      state.tags.data = tags
      state.tags.pagination = pagination ? pagination : { total }
      state.tags.status = constants.COMPLETE
    },

    searchTagsError: (state, action) => {
      searchError('tags', state, action.payload.error)
    },

    // ? Page tags (results page topic strip)
    /* Clears as it starts: the strip belongs to one term, so the previous term's
       topics must not sit there looking current while the next ones load. */
    pageTagsStart: state => {
      searchReset('pageTags', state)
      searchStart('pageTags', state)
    },

    /* Replaces rather than appends: the strip shows one term's topics, and the
       term it is showing them for can change at any time. */
    pageTagsSuccess: (state, action) => {
      const { results, pagination, total } = action.payload
      state.pageTags.data = results ?? []
      state.pageTags.pagination = pagination ? pagination : { total }
      state.pageTags.status = constants.COMPLETE
    },

    pageTagsError: (state, action) => {
      searchError('pageTags', state, action.payload?.error ?? action.payload)
    },
  },
})

export const {
  searchMarketsReset,
  searchMarketsStart,
  searchMarketsSuccess,
  searchMarketsError,
  startMarketsLoading,
  searchForecasterReset,
  searchForecastersStart,
  searchForecastersSuccess,
  searchForecastersError,
  startForecastersLoading,
  startTagsLoading,
  searchCategoriesStart,
  searchCategoriesSuccess,
  searchCategoriesError,
  searchCategoriesReset,
  searchTagsStart,
  searchTagsSuccess,
  searchTagsError,
  searchTagsReset,
  pageTagsStart,
  pageTagsSuccess,
  pageTagsError,
} = search.actions

export default search.reducer

// Selectors:

export const selectMarkets = () => state => state.search.markets.data
export const selectForecasters = () => state => state.search.forecasters.data
export const selectCategories = () => state =>state.search.categories.data

export const selectTags = () => state => state.search.tags.data

/* Optional access on purpose: a server-rendered payload minted before this slot
   existed replaces the slice wholesale, so the key can be absent on hydrate. */
export const selectPageTags = () => state => state.search.pageTags?.data ?? []
export const selectPageTagsStatus = () => state => state.search.pageTags?.status ?? constants.IDLE

export const selectMarketsStatus = () => state => state.search.markets.status
export const selectCategoriesStatus = () => state => state.search.categories.status
export const selectForecastersStatus = () => state => state.search.forecasters.status
export const selectTagsStatus = () => state => state.search.tags.status

export const selectMarketsPagination = () => state => state.search.markets.pagination || {}
export const selectForecastersPagination = () => state => state.search.forecasters.pagination || {}
export const selectCategoriesPagination = () => state => state.search.categories.pagination || {}
export const selectTagsPagination = () => state => state.search.tags.pagination || {}

export const resetSearch = () => dispatch => {
  dispatch(searchMarketsReset())
  dispatch(searchForecasterReset())
  dispatch(searchCategoriesReset())
  dispatch(searchTagsReset())
}

export const searchMarkets = (query = '', params = {}) => async dispatch => {

  const qs = createQueryString(query, params)
  const url = `/questions/?${qs}`

  dispatch(searchMarketsStart())

  try {
    const response = await requestService.get(url)
    const data = await response.json()

    await dispatch(searchMarketsSuccess(data))
  }
  catch (err) {
    toastService('fail', buildErrorToast(err, 'api_errors.titles.search'))
    dispatch(searchMarketsError(err))
  }
}

export const searchForecasters = (query = '', params = {}) => async dispatch => {
  const qs = createQueryString(query, params)
  const url = `/users/?${qs}`

  dispatch(searchForecastersStart())

  try {
    const response = await requestService.get(url)
    const data = await response.json()

    dispatch(searchForecastersSuccess(data))
  }
  catch (err) {
    toastService('fail', buildErrorToast(err, 'api_errors.titles.search'))
    dispatch(searchForecastersError(err))
  }
}

/* No toast on failure: topics are a secondary strip in the dropdown, and the
   market and forecaster results behind them are still usable without it. */
export const searchCategories = (query = '', params = {}) => async dispatch => {
    const currencyMode = getCurrencyMode(params.currencyId)
    const url = `/categories/?search=${encodeURIComponent(query)}&currency_mode=${currencyMode}`

    dispatch(searchCategoriesStart())

    try {
      const response = await requestService.get(url)
      const data = await response.json()

      dispatch(searchCategoriesSuccess(data))
    }
    catch (err) {
      dispatch(searchCategoriesError(err))
    }
  }

/* No toast on failure, for the same reason as `searchCategories`. */
export const searchTags = (query = '', params = {}) => async dispatch => {
  const currencyMode = getCurrencyMode(params.currencyId)
  const url = `/tags/?search=${encodeURIComponent(query)}&currency_mode=${currencyMode}`

  dispatch(searchTagsStart())

  try {
    const response = await requestService.get(url)
    const data = await response.json()

    dispatch(searchTagsSuccess(data))
  }
  catch (err) {
    dispatch(searchTagsError(err))
  }
}

/* Same endpoint as `searchTags`, separate slot. No toast on failure: this feeds a
   secondary strip, and the feed behind it is still usable without it. */
export const searchPageTags = (query = '', params = {}) => async dispatch => {
  const currencyMode = getCurrencyMode(params.currencyId)
  const url = `/tags/?search=${encodeURIComponent(query)}&currency_mode=${currencyMode}`

  // Navigation-driven, so two terms are easily in flight at once: drop the
  // earlier one rather than let it settle over the newer term's topics.
  currentPageTagsAbortController?.abort()
  currentPageTagsAbortController = new AbortController()
  const { signal } = currentPageTagsAbortController

  dispatch(pageTagsStart())

  try {
    const response = await requestService.get(url, { signal })
    const data = await response.json()

    dispatch(pageTagsSuccess(data))
  }
  catch (err) {
    if (err?.reason_name === 'AbortError') return
    dispatch(pageTagsError({ error: err }))
  }
}

export const searchAll = (query = {}, params = {}) => async dispatch => {
    const searchParams = []
    query.search = query.q || ''
    delete query.q
    for (const property in query) {
      searchParams.push(property + '=' + encodeURIComponent(query[property]))
    }
    const currencyMode = getCurrencyMode(params.currencyId)
    const url = `/search/?${searchParams.join('&')}&currency_mode=${currencyMode}`

    dispatch(searchMarketsStart())
    dispatch(searchCategoriesStart())
    dispatch(searchTagsStart())

    try {
      var response = await requestService.get(url)
    } catch (error) {
      dispatch(searchMarketsError({ error }))
      dispatch(searchCategoriesError({ error }))
      dispatch(searchTagsError({ error }))
    }

    const json = await response.json()
    dispatch(addMarketsToPool({ markets: json.questions }))
    dispatch(searchMarketsSuccess({ results: json.questions, total: json.questions_total }))

    dispatch(addCategoriesToPool({ categories: json.categories }))
    dispatch(searchCategoriesSuccess({ results: json.categories, total: json.categories_total }))

    dispatch(addTagsToPool({ tags: json.tags }))
    dispatch(searchTagsSuccess({ results: json.tags, total: json.tags_total }))
  }
